<template>
  <div class="row justify-content-center">
    <div class="col-md-8 col-lg-6 col-xl-7 col-xxl-6 col-xxxl-5">
      <div class="card mb-25 border-0 rounded-0 bg-white authentication-card">
        <div class="card-body letter-spacing">
          <div class="text-center mb-10">
            <img src="../../../assets/images/logo-eitp.png" alt="logo-icon" style="height: 100px;"/>
          </div>
          <h4 class="text-black fw-bold mb-0 text-center">Réinitialiser le mot de passe</h4>
          <p class="mb-0 fs-md-15 fs-lg-16 text-paragraph text-center sub-text">
            Entrez votre nouveau mot de passe et confirmez-le dans le champ ci-dessous
          </p>

          <!-- Message de succès -->
          <div v-if="successMessage" class="alert alert-success mt-3" role="alert">
            {{ successMessage }}
          </div>

          <!-- Message d'erreur -->
          <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
            {{ errorMessage }}
          </div>

          <Form ref="resetForm" @submit="addReset" :validation-schema="resetSchema">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Mot de passe 
              </label>
              <Field 
                name="password" 
                type="password" 
                class="form-control shadow-none fs-md-15 text-black" 
                placeholder="Entrer le mot de passe"
                :disabled="loading"
              />
              <ErrorMessage name="password" class="text-danger"/>
            </div>
            
            <div class="form-group mb-15 mb-sm-20">
              <label class="d-block text-black fw-semibold mb-10">
                Confirmer le mot de passe
              </label>
              <Field 
                name="password_confirm" 
                type="password" 
                class="form-control shadow-none fs-md-15 text-black" 
                placeholder="Confirmer le mot de passe"
                :disabled="loading"
              />
              <ErrorMessage name="password_confirm" class="text-danger"/>
            </div>

            <button
              class="default-btn transition border-0 fw-medium text-white rounded-1 fs-md-15 fs-lg-16 d-block w-100"
              type="submit"
              :disabled="loading"
            >
              <span v-if="loading">
                <i class="ph-bold ph-circle-notch ph-spin"></i> Chargement...
              </span>
              <span v-else>
                Réinitialiser le mot de passe
              </span>
            </button>

            <div class="text-center mt-15 mt-sm-20 mt-md-25">
              <router-link
                to="/login"
                class="link-btn text-decoration-none text-primary fw-medium fs-md-15 fs-lg-16 position-relative"
              >
                <i class="ph-bold ph-arrow-left"></i>
                Revenir à la page de connexion
              </router-link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import axios from 'axios';

export default defineComponent({
  name: "ResetPassword",
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  setup: () => {
    const router = useRouter();
    const route = useRoute();

    // États
    const resetForm = ref(null);
    const loading = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const token = ref('');

    // Schéma de validation
    const resetSchema = Yup.object().shape({
      password: Yup.string()
        .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]/,
          'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
        )
        .required('Le mot de passe est obligatoire'),
      password_confirm: Yup.string()
        .oneOf([Yup.ref('password')], 'Les mots de passe ne correspondent pas')
        .required('La confirmation est obligatoire'),
    });

    // Récupérer le token depuis l'URL au montage du composant
    onMounted(() => {
      token.value = route.query.token as string;
      
      if (!token.value) {
        errorMessage.value = 'Token manquant. Veuillez utiliser le lien reçu par email.';
      }
    });

    // Fonction de soumission
    const addReset = async (values: any) => {
      // Réinitialiser les messages
      successMessage.value = '';
      errorMessage.value = '';

      // Vérifier le token
      if (!token.value) {
        errorMessage.value = 'Token manquant. Veuillez utiliser le lien reçu par email.';
        return;
      }

      loading.value = true;

      try { 
        // Appel API
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/api/users/reinitialiser-mot-de-passe`,
          {
            token: token.value,
            newPassword: values.password,
          }
        );

        // Succès
        successMessage.value = response.data.message || 'Mot de passe défini avec succès !';
        
        // Réinitialiser le formulaire
        if (resetForm.value) {
          (resetForm.value as any).resetForm();
        }

        // Rediriger vers la page de connexion après 2 secondes
        setTimeout(() => {
          router.push('/login');
        }, 2000);

      } catch (error: any) {
        console.error('Erreur lors de la réinitialisation:', error);

        // Gestion des erreurs
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage.value = 'Token invalide ou expiré. Veuillez demander un nouveau lien.';
          } else if (error.response.status === 400) {
            errorMessage.value = error.response.data.message || 'Token expiré. Veuillez demander un nouveau lien.';
          } else {
            errorMessage.value = error.response.data.message || 'Une erreur est survenue. Veuillez réessayer.';
          }
        } else if (error.request) {
          errorMessage.value = 'Impossible de contacter le serveur. Vérifiez votre connexion internet.';
        } else {
          errorMessage.value = 'Une erreur inattendue est survenue.';
        }
      } finally {
        loading.value = false;
      }
    };

    return { 
      resetSchema, 
      addReset, 
      resetForm,
      loading,
      successMessage,
      errorMessage,
    };
  },
});
</script>

<style scoped>
/* Styles optionnels pour améliorer l'UI */
.alert {
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 14px;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ph-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>